import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Icon, ICON_SIZE, type IconName } from '../chrome/icons';
import { Spinner, ToolbarButton, ToolbarGroup, ToolbarSeparator } from '../../ui/controls';
import { AvatarStack, CollabStatusPill, type Follow, type UiRoom } from '@flobi/collab-client/react';
import { Menu, useMenu, type MenuEntry } from '../../ui/menu';
import { Popover } from '../../ui/popover';
import { ExportPanel, type ExportOptions } from './ExportPanel';
import { tip } from '../../ui/tooltip';
import type { ToolId } from '../useEditorCanvas';
import { ADDONS_HIDDEN } from '../../lib/hiddenFeatures';

/** Document controls and a persistent tool shelf above the infinite canvas. */

export interface ToolMark {
  readonly id: ToolId | 'shape';
  readonly label: string;
  readonly icon: IconName;
  readonly shortcut?: string;
}

/** The tools in the bar: the ones that make things. */
export const EVERYDAY_TOOLS: readonly ToolMark[] = [
  { id: 'select', label: 'Select', icon: 'cursor', shortcut: 'V' },
  { id: 'pen', label: 'Pen', icon: 'pen', shortcut: 'P' },
  { id: 'draw', label: 'Draw', icon: 'draw', shortcut: 'D' },
  { id: 'shape', label: 'Shape', icon: 'square', shortcut: 'M' },
  { id: 'text', label: 'Text', icon: 'text', shortcut: 'T' },
];

/** The tools behind More: the ones that change what is drawn, and the two that measure it. */
export const SPECIALIST_TOOLS: readonly (readonly ToolMark[])[] = [
  [
    // Plain words, and none of Adobe's (the repo's own rule): Reshape, Combine, Distort,
    // Repeat and Export areas say what they do to somebody who has never opened Illustrator.
    { id: 'artboard', label: 'Artboard', icon: 'artboard', shortcut: '⇧O' },
    { id: 'warp', label: 'Distort', icon: 'free-transform', shortcut: 'R' },
    { id: 'liquify', label: 'Reshape', icon: 'warp', shortcut: '⇧R' },
    { id: 'blend', label: 'Blend', icon: 'blend', shortcut: 'W' },
    { id: 'cloner', label: 'Repeat', icon: 'cloner', shortcut: '⇧C' },
    { id: 'builder', label: 'Combine', icon: 'combine', shortcut: '⇧M' },
    { id: 'cut', label: 'Cut', icon: 'eraser', shortcut: 'E' },
  ],
  [
    { id: 'measure', label: 'Measure', icon: 'dimension', shortcut: '⇧K' },
    { id: 'slice', label: 'Export areas', icon: 'slice', shortcut: 'K' },
  ],
];

export const ALL_TOOLS: readonly ToolMark[] = [...EVERYDAY_TOOLS, ...SPECIALIST_TOOLS.flat()];

/** Where a tool id lives in the toolbar: the shapes share one mark. */
export function markOf(tool: ToolId, shapeKinds: readonly ToolId[]): ToolMark['id'] {
  if (shapeKinds.includes(tool)) return 'shape';
  // Width is the liquify family's first brush; it wears liquify's mark.
  if (tool === 'width') return 'liquify';
  return tool;
}

/**
 * What is out beside the canvas: the side panel (`format`), or Animate — which is not a
 * panel but a bar along the bottom, standing where the tools were while it is on.
 */
export type PanelView = 'format' | 'animate';

export function Toolbar({
  name,
  onRename,
  documentMenu,
  onDocumentMenu,
  settings,
  onSettingsClose,
  exportOptions,
  zoomMenu,
  zoom,
  room,
  follow,
  readOnly,
  tool,
  shapeIcon,
  onTool,
  hiddenTools,
  onFiles,
  adding,
  onAdd,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  animating,
  onAnimate,
  addonInHand,
  onAddons,
  onZoomBy,
  exporting,
  dock,
  onShare,
  bell,
  center,
  onPresent,
  inspecting,
  onInspect,
  onHeight,
  onFit,
  navSlot,
  foldNav = false,
  onPan,
  panning = false,
  phone = false,
  navExtra,
}: {
  /**
   * A phone (Artwork A, 2026-10-26): a way back to Home leads the bar, which keeps the title, undo,
   * redo, the bell and Export; Properties, Comment and Share are in the title menu. The zoom pill
   * keeps the zoom menu and Fit — a pinch zooms and two fingers pan.
   */
  phone?: boolean;
  /** Beside the zoom pill, on its row: the Shift and Option chips on a touch screen. */
  navExtra?: ReactNode;
  onFit?: () => void;
  /** A place at the end of the zoom pill for others to fill (Generate's progress bar). */
  navSlot?: (element: HTMLElement | null) => void;
  /** The zoom pill folded to a run's bar alone: the dock needs its room (EditorPage measures). */
  foldNav?: boolean;
  onPan?: () => void;
  panning?: boolean;
  onHeight: (height: number) => void;
  dock?: ReactNode;
  /** The file's Share popup (its project's people, and its view link). Absent, there is no Share button. */
  onShare?: () => void;
  /** Flobi's notifications bell, drawn by the page: a phone's top bar keeps it. */
  bell?: ReactNode;
  /** The middle of the bar: the selection's bar, or a tool's options. */
  center?: ReactNode;
  /** Opens the artboards' full view in a tab of its own. */
  onPresent?: () => void;
  /** Whether the Properties panel is out, and the way to bring it out or put it away. */
  inspecting?: boolean;
  onInspect?: () => void;
  name: string;
  onRename: (name: string) => void;
  documentMenu: readonly MenuEntry[];
  /** The document menu has just opened: what it lists can be read again (Version history). */
  onDocumentMenu?: () => void;
  /** Document Settings, when the document menu has asked for them: hangs from the title. */
  settings: ReactNode | null;
  onSettingsClose: () => void;
  exportOptions: ExportOptions;
  zoomMenu: readonly MenuEntry[];
  zoom: number;
  /** The page's room: who is here (never this person's own other tabs), and the connection. */
  room: UiRoom | null;
  /** Click an avatar to follow that person's view. */
  follow: Follow;
  /** A viewer: no tools, no editing, no panels — they look, zoom and export. */
  readOnly: boolean;
  /** The tool's mark in the toolbar. */
  tool: ToolMark['id'];
  shapeIcon: IconName;
  onTool: (id: ToolMark['id']) => void;
  hiddenTools: ReadonlySet<string>;
  onFiles: () => void;
  /** Whether the library — what Add opens — is out. */
  adding: boolean;
  onAdd: () => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  animating: boolean;
  onAnimate: () => void;
  /** The add-on whose tool is in hand, by name, or null: it shows in the dock like any tool. */
  addonInHand: string | null;
  /** Opens the library on its Add-ons. */
  onAddons: () => void;
  /** Zooms in or out by a factor, about the middle of the view. */
  onZoomBy: (factor: number) => void;
  /** An export going on in the cloud: shown on the Export button itself. */
  exporting: number | null;
}) {
  const header = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const element = header.current;
    if (!element) return;
    const measure = () => onHeight(Math.ceil(element.getBoundingClientRect().bottom));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [onHeight]);
  const markSize = phone ? ICON_SIZE.control : ICON_SIZE.small;
  const doc = useMenu();
  const documentMenuOpened = useRef(onDocumentMenu);
  documentMenuOpened.current = onDocumentMenu;
  useEffect(() => {
    if (doc.open) documentMenuOpened.current?.();
  }, [doc.open]);
  const out = useMenu();
  const zoomer = useMenu();
  const more = useMenu();
  /** Compact widths keep panel and sharing icons; Export retains its label. */
  const compact = useNarrow(940);

  // ⇧⌘E asks for this menu from anywhere, as File ▸ Export would.
  const openExport = out.openMenu;
  useEffect(() => {
    window.addEventListener('artwork:export', openExport);
    return () => window.removeEventListener('artwork:export', openExport);
  }, [openExport]);

  const everyday = EVERYDAY_TOOLS.filter((mark) => !hiddenTools.has(mark.id));
  const specialist = SPECIALIST_TOOLS.map((run) => run.filter((mark) => !hiddenTools.has(mark.id)));
  const held = specialist.flat().find((mark) => mark.id === tool);
  const moreItems: MenuEntry[] = [
    ...specialist.flatMap((run, index) => [
      ...(index > 0 ? [{ kind: 'separator' as const }] : []),
      ...run.map((mark) => ({ id: mark.id, label: mark.label, icon: mark.icon, shortcut: mark.shortcut, checked: tool === mark.id, onSelect: () => onTool(mark.id) })),
    ]),
    // Add-ons bring tools and commands of their own: they are one more place tools come from.
    ...(ADDONS_HIDDEN
      ? []
      : [{ kind: 'separator' as const }, { id: 'addons', label: 'Add-ons…', icon: 'addons' as const, onSelect: onAddons }]),
  ];

  return (
    <>
      <div ref={header} className="workspace-header">
      <div className="artwork-topbar pointer-events-none absolute inset-x-[8px] top-[8px] z-30 flex items-center gap-[8px]">
        <div className="artwork-topbar-left flex min-w-0 items-center gap-[8px]">
        {phone ? <ToolbarGroup label="Back" className="artwork-topbar-group shrink-0">
          <ToolbarButton icon="back" label="Home" className="artwork-topbar-icon" onClick={onFiles} />
        </ToolbarGroup> : null}
        <ToolbarGroup label="Document" className="artwork-topbar-group artwork-topbar-document min-w-0 shrink">
          <DocumentTitle name={name} onRename={onRename} menuRef={doc.ref} onOpenMenu={doc.toggle} menuOpen={doc.open} readOnly={readOnly} />
          {phone ? <Presence room={room} follow={follow} /> : null}
        </ToolbarGroup>
        <Menu open={doc.open} anchor={doc.anchor} onClose={doc.close} items={[
          { id: 'home', label: 'Home', onSelect: onFiles },
          { kind: 'separator' },
          ...documentMenu,
        ]} label="Document" width={220} />
        <Popover open={settings !== null} anchor={doc.anchor} onClose={onSettingsClose} placement="below" align="start" width={300} label="Document Settings">
          {settings}
        </Popover>

        {!readOnly && <ToolbarGroup label="History" className="artwork-topbar-group artwork-topbar-history shrink-0">
          <ToolbarButton icon="toolbar-undo" label="Undo" shortcut="⌘Z" className="artwork-topbar-icon" iconSize={markSize} onClick={onUndo} disabled={!canUndo} />
          <ToolbarButton icon="toolbar-redo" label="Redo" shortcut="⇧⌘Z" className="artwork-topbar-icon" iconSize={markSize} onClick={onRedo} disabled={!canRedo} />
        </ToolbarGroup>}
        </div>
        {/* The selection's bar or a tool's options, always here (Tarek, 1 Nov), in the middle of the window. */}
        {phone ? <span className="artwork-topbar-spacer flex-1" /> : <div className="artwork-topbar-center">{center}</div>}

        <div className="artwork-topbar-actions flex shrink-0 items-center gap-[8px]">
          {/* Who is here, Properties, Play, Share and Export (Tarek, 1 Nov, after Figma's). Comment is in the dock; a phone keeps the bell. */}
          {phone ? bell ? <ToolbarGroup label="Notifications" className="artwork-topbar-group shrink-0"><span className="toolbar-bell contents">{bell}</span></ToolbarGroup> : null
            : <div className="pointer-events-auto flex min-w-0 items-center"><Presence room={room} follow={follow} /></div>}
          {!phone ? <ToolbarGroup label="View" className="artwork-topbar-group shrink-0">
            {!readOnly && onInspect ? <ToolbarButton icon="toolbar-properties" label="Properties" shortcut="⌥⌘I" className="artwork-topbar-properties artwork-topbar-icon" iconSize={markSize} selected={!!inspecting} onClick={onInspect} /> : null}
            {onPresent ? <ToolbarButton icon="present" label="Present" className="artwork-topbar-icon" iconSize={markSize} onClick={onPresent} /> : null}
          </ToolbarGroup> : null}

          {onShare && !phone ? <ToolbarGroup label="Share" className="artwork-topbar-group shrink-0">
            <ToolbarButton icon="toolbar-share" label="Share" shortcut="⇧⌘S" className={`artwork-topbar-share ${compact ? 'artwork-topbar-icon' : ''}`} iconSize={markSize} showLabel={!compact} aria-haspopup="dialog" onClick={onShare} data-tour="artwork.share" />
          </ToolbarGroup> : null}

          <button
            ref={out.ref}
            type="button"
            aria-haspopup="dialog"
            aria-expanded={out.open}
            onClick={out.toggle}
            {...tip('Export', '⇧⌘E')}
            aria-busy={!!exportOptions.activity || exporting !== null}
            data-tour="artwork.export"
            aria-label={phone ? 'Export' : undefined}
            className="artwork-topbar-export pointer-events-auto relative flex shrink-0 items-center justify-center gap-[7px] overflow-hidden text-[13px] font-medium"
          >
            {exporting !== null ? (
              <span aria-hidden className="artwork-topbar-progress absolute inset-y-0 left-0 transition-[width] duration-300" style={{ width: `${Math.max(6, exporting)}%` }} />
            ) : null}
            <span className="relative">
              {exportOptions.activity ? <Spinner size={18} /> : <Icon name="toolbar-export" size={markSize} />}
            </span>
            {phone && !exportOptions.activity && exporting === null ? null : <span className="relative">{exportOptions.activity ? 'Exporting…' : exporting !== null ? `${exporting}%` : 'Export'}</span>}
          </button>
          <ExportPanel open={out.open} anchor={out.anchor} onClose={out.close} options={exportOptions} progress={exporting} />
        </div>
      </div>

      </div>

      {/* The dock floats at the bottom, centred: the tools and the two modes (CreateEditDock).
          The lab, which hands no dock over, draws the everyday tools itself. */}
      {!readOnly ? <div className="workspace-dock pointer-events-none">
        {dock ?? <ToolbarGroup label="Tools" className="shrink-0 pointer-events-auto">
          {everyday.map((mark) => (
            <ToolbarButton
              key={mark.id}
              icon={mark.id === 'shape' ? shapeIcon : mark.icon}
              label={mark.label}
              shortcut={mark.shortcut}
              tone="accent"
              selected={tool === mark.id}
              onClick={() => onTool(mark.id)}
            />
          ))}
          <ToolbarSeparator />
          <ToolbarButton icon="plus" label="Add Assets" shortcut="⌃⌘S" tone="soft" selected={adding} onClick={onAdd} />
          {held ? <ToolbarButton icon={held.icon} label={held.label} shortcut={held.shortcut} tone="accent" selected onClick={() => onTool(held.id)} /> : null}
          {addonInHand !== null ? <ToolbarButton icon="addons" label={`${addonInHand} — an add-on`} tone="accent" selected onClick={onAddons} /> : null}
          <ToolbarButton ref={more.ref} icon="more" label="More Tools" aria-haspopup="menu" aria-expanded={more.open} onClick={more.toggle} />
          <Menu open={more.open} anchor={more.anchor} onClose={more.close} placement="above" align="center" label="More Tools" width={220} items={moreItems} />
        </ToolbarGroup>}
      </div> : null}

      <div className="workspace-canvas-nav pointer-events-none">
        <div role="group" aria-label="Canvas navigation" className="canvas-nav-controls" data-folded={foldNav || undefined}>
          {!foldNav && <>
          {onPan && !phone && <CanvasNavButton icon="hand" label="Pan canvas" shortcut="Space" selected={panning} onClick={onPan} />}
          {phone ? null : <CanvasNavButton icon="minus" label="Zoom Out" shortcut="⌘−" onClick={() => onZoomBy(1 / 1.25)} />}
          <button
            ref={zoomer.ref}
            type="button"
            aria-haspopup="menu"
            aria-expanded={zoomer.open}
            aria-label={`Zoom, ${Math.round(zoom * 100)}%`}
            {...tip('Zoom')}
            onClick={zoomer.toggle}
            className="canvas-nav-button canvas-nav-zoom tnum"
          >
            {Math.round(zoom * 100)}%
          </button>
          {phone ? null : <CanvasNavButton icon="plus" label="Zoom In" shortcut="⌘+" onClick={() => onZoomBy(1.25)} />}
          {onFit && <><span className="canvas-nav-divider" aria-hidden /><CanvasNavButton icon="fit" label="Fit all" showLabel={!phone} onClick={onFit} /></>}
          </>}
          {navSlot && <span ref={navSlot} className="canvas-nav-slot" />}
        </div>
        <Menu open={zoomer.open} anchor={zoomer.anchor} onClose={zoomer.close} items={zoomMenu} placement="above" align="start" label="Zoom" width={200} />
        {navExtra}
      </div>
      {/* Elsewhere Animate is a mode in the dock; a phone has no dock, so here it stands beside the Agent orb. */}
      {phone && !readOnly && !hiddenTools.has('animate') && <div className="workspace-canvas-animate pointer-events-none">
        <button type="button" className="canvas-corner-pill pointer-events-auto" aria-pressed={animating} data-tour="artwork.animate" {...tip('Animate')} onClick={onAnimate}>
          <Icon name="motion" size={18} /><span>Animate</span>
        </button>
      </div>}
    </>
  );
}

function CanvasNavButton({ icon, label, shortcut, selected, showLabel, onClick }: { icon: IconName; label: string; shortcut?: string; selected?: boolean; showLabel?: boolean; onClick: () => void }) {
  return <button type="button" className={`canvas-nav-button${showLabel ? ' canvas-nav-labeled' : ''}`} aria-label={label} aria-pressed={selected} {...tip(label, shortcut)} onClick={onClick}>
    <Icon name={icon} size={18} />{showLabel && <span>{label}</span>}
  </button>;
}

/**
 * The document's name, with the document menu hanging from it — Rename, the view and the
 * settings — as a Mac document window's title does. A double-click renames it in place.
 */
function DocumentTitle({
  name,
  onRename,
  menuRef,
  onOpenMenu,
  menuOpen,
  readOnly,
}: {
  name: string;
  onRename: (name: string) => void;
  menuRef: (element: HTMLElement | null) => void;
  onOpenMenu: () => void;
  menuOpen: boolean;
  /** A viewer cannot rename the file: the title opens its menu and nothing else. */
  readOnly: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  /**
   * What the field held when editing began. A rename goes out only if the person changed
   * that text: comparing with the CURRENT name meant that a colleague's rename, arriving
   * while this field sat open, was undone by simply clicking away (QA 2026-09-24,
   * files-doc-F18).
   */
  const [began, setBegan] = useState(name);
  const start = () => {
    if (readOnly) return;
    setDraft(name);
    setBegan(name);
    setEditing(true);
  };

  // The menu's Rename asks for this by event, since the title owns its own field.
  useEffect(() => {
    window.addEventListener('artwork:rename', start);
    return () => window.removeEventListener('artwork:rename', start);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next && next !== began.trim()) onRename(next);
  };

  if (editing) {
    return (
      <input
        autoFocus
        aria-label="Document name"
        // The server keeps names to 256 characters; a longer one was refused (files-doc-F17).
        maxLength={256}
        value={draft}
        onFocus={(event) => event.currentTarget.select()}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') commit();
          if (event.key === 'Escape') {
            setDraft(name);
            setEditing(false);
          }
          event.stopPropagation();
        }}
        className="mx-[4px] h-[28px] w-[200px] min-w-0 rounded-[8px] bg-fill-1 px-[8px] text-[13px] font-semibold text-label outline-none"
      />
    );
  }
  return (
    <button
      ref={menuRef}
      type="button"
      aria-haspopup="menu"
      aria-expanded={menuOpen}
      onClick={onOpenMenu}
      onDoubleClick={start}
      {...tip('Document')}
      className={`flex h-[36px] min-w-0 items-center gap-[5px] rounded-[18px] px-[10px] transition-colors hover:bg-fill-2 pointer-coarse:h-[44px] ${menuOpen ? 'bg-fill-selected' : ''}`}
    >
      <span className="max-w-[220px] truncate text-[13px] font-semibold text-label max-[940px]:max-w-[110px]">{name}</span>
      <span className="shrink-0 text-label-2">
        <Icon name="toolbar-chevron" size={10} />
      </span>
    </button>
  );
}

/**
 * Who else is in the file, and a word only when the connection is not what it should be. A file
 * that saves is not news: nothing shows while it is connected and nobody else is here. The kit's
 * avatars are the collab standard (three and a count, click to follow); the look is this app's,
 * through the kit's CSS variables.
 */
const COLLAB_LOOK = {
  '--collab-avatar-size': '20px',
  '--collab-ring': 'var(--glass-thick)',
  '--collab-font-size': '10px',
} as React.CSSProperties;

function Presence({ room, follow }: { room: UiRoom | null; follow: Follow }) {
  return (
    <div className="flex min-w-0 shrink-0 items-center gap-[8px] pl-[2px] pr-[8px] empty:hidden" style={COLLAB_LOOK}>
      <AvatarStack room={room} follow={follow} />
      <CollabStatusPill
        room={room}
        unstyled
        className="min-w-0 truncate whitespace-nowrap text-[12px] font-medium text-label-2 data-[collab-status=offline]:text-warning"
      />
    </div>
  );
}

/** Whether the window is narrower than a width, kept up to date as it is resized. */
function useNarrow(width: number): boolean {
  const [narrow, setNarrow] = useState(() => window.innerWidth < width);
  useEffect(() => {
    const check = () => setNarrow(window.innerWidth < width);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [width]);
  return narrow;
}
